import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Student } from '../../types';
import {
  Search,
  ArrowRight,
  Download,
  Users,
  AlertCircle
} from 'lucide-react';
import { formatDate, getAge, exportStudentsToExcel } from '../../utils/helpers';
import { sortClassNames } from '../../utils/classUtils';

interface StudentListProps {
  students: Student[];
  loading?: boolean;
}

const StudentList: React.FC<StudentListProps> = ({
  students,
  loading = false
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 10;

  // Filter and search students
  const filteredStudents = students.filter(student => {
    const matchesSearch =
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.studentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.parentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.parentContact.includes(searchTerm);

    const matchesClass = !filterClass || student.class === filterClass;
    const matchesStatus = !filterStatus || student.admissionStatus === filterStatus;

    return matchesSearch && matchesClass && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, startIndex + itemsPerPage);

  const classes = sortClassNames(Array.from(new Set(students.map(s => s.class))));

  const handleExport = () => {
    exportStudentsToExcel(filteredStudents);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Approved':
        return 'bg-[#173322] text-[#8ff0b8] border border-[#1f4a30]';
      case 'Pending':
        return 'bg-[#332711] text-[#f9daa3] border border-[#4a3a19]';
      case 'Rejected':
        return 'bg-[#341a17] text-[#fbccc8] border border-[#5a2b26]';
      default:
        return 'bg-[#f3ede2] text-[#4a4235]';
    }
  };

  if (loading) {
    return (
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
      {/* Header */}
      <div className="p-6 border-b border-[#3a3122]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-[#f3ede2] flex items-center">
              <Users className="w-6 h-6 mr-2 text-[#e2916d]" />
              Students ({filteredStudents.length})
            </h2>
            <p className="text-[#b9ad9b] mt-1">Manage student registrations and admissions</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExport}
              className="flex items-center space-x-2 px-4 py-2 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#211c15] transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            />
          </div>

          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            className="px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
          >
            <option value="">All Classes</option>
            {classes.map(cls => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {paginatedStudents.length === 0 ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#f3ede2] mb-2">No students found</h3>
            <p className="text-[#b9ad9b]">
              {searchTerm || filterClass || filterStatus
                ? 'Try adjusting your search criteria'
                : 'Students appear here once their registration is approved and admission is completed'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#211c15]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#b9ad9b] uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#b9ad9b] uppercase tracking-wider">
                  Class/Section
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#b9ad9b] uppercase tracking-wider">
                  Age
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#b9ad9b] uppercase tracking-wider">
                  Parent Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#b9ad9b] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#b9ad9b] uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[#b9ad9b] uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#3a3122]">
              {paginatedStudents.map((student) => (
                <tr
                  key={student.id}
                  onClick={() => navigate(`/admin/students/${student.id}`)}
                  className="hover:bg-[#211c15] transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      {student.photoURL ? (
                        <img
                          src={student.photoURL}
                          alt={student.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-[#e2916d] rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {student.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-[#f3ede2]">{student.name}</p>
                        <p className="text-sm text-[#8a7f6e]">{student.studentId}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-[#f3ede2]">{student.class}</p>
                    {student.section && (
                      <p className="text-sm text-[#8a7f6e]">Section {student.section}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-[#f3ede2]">
                    {getAge(student.dateOfBirth)} years
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-[#f3ede2]">{student.parentName}</p>
                    <p className="text-sm text-[#8a7f6e]">{student.parentContact}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(student.admissionStatus)}`}>
                      {student.admissionStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#8a7f6e]">
                    {formatDate(student.createdAt)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/students/${student.id}`);
                      }}
                      className="p-2 text-[#8a7f6e] hover:text-[#e2916d] hover:bg-[#28221a] rounded-lg transition-colors"
                      title="View student details"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-[#3a3122] flex items-center justify-between">
          <div className="text-sm text-[#b9ad9b]">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredStudents.length)} of {filteredStudents.length} students
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-[#3a3122] text-[#b9ad9b] rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#211c15]"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-[#f3ede2]">
              {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-[#3a3122] text-[#b9ad9b] rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#211c15]"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentList;
